import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import FullCalendar, {
    CustomContentGenerator,
    EventContentArg,
    EventInput,
    EventSourceInput,
    ViewApi
} from '@fullcalendar/react'; // must go before plugins
import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin!
import timeGridPlugin from '@fullcalendar/timegrid'; // a plugin!
import listPlugin from '@fullcalendar/list'; // a plugin!
import momentPlugin from '@fullcalendar/moment'; // a plugin!
import Select from 'react-select';

import createStore from './store';
import { calendarApi } from '../../features/calendar';
import { FilterEventEnum } from '../../models/filter';
import { DateUtility, DayJSDateStringFormat } from '../../utilities/date';
import { ICalendarEvent } from '../../models/event';
import { Spinner } from '../../components/spinner';
import ListMonthView from '../../calendar-views/list-month';
import WeekView from '../../calendar-views/week';
import { ApiUtility } from '../../utilities/api';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { ErrorBoundary } from '../../components/error-boundary';
import { Button, LinkButton } from '../../components/button';
import { DatePicker } from '../../components/date-picker';
import { ISelectOption, UskinnedSelectStyles, IGroupSelectOption} from '../../models/select';
import { Alert, AlertType } from '../../components/alert';
import { StringUtility } from '../../utilities/string';
import { ClipboardUtility } from '../../utilities/clipboard';
import { ObjectUtility } from '../../utilities/object';
import MonthView from '../../calendar-views/month';
import DayView from '../../calendar-views/day';

const enum CalendarViewType {
    DayGridMonth = 'dayGridMonth',
    TimeGridWeek = 'timeGridWeek',
    TimeGridDay = 'timeGridDay',
    ListMonth = 'listMonth'
}

// Custom event content renderer for each calendar view
// NOTE: This will only replace the view of the event content
// Other parts of the calendar UI will still remain
const EventContentRenderer: CustomContentGenerator<EventContentArg> = eventInfo => {
    if (eventInfo.view.type === CalendarViewType.ListMonth) {
        return <ListMonthView {...eventInfo} />;
    }
    else if (eventInfo.view.type === CalendarViewType.TimeGridWeek) {
        return <WeekView {...eventInfo} />;
    } else if (eventInfo.view.type === CalendarViewType.DayGridMonth) {
        return <MonthView {...eventInfo} />;
    } else if (eventInfo.view.type === CalendarViewType.TimeGridDay) {
        return <DayView {...eventInfo} />;
    }

    // return nothing to keep the default content view
};

interface ICalendarTimeConversionResult {
    isActiveDate: boolean;
    dateStr: string;
}

/**
 * convert the date time timezone from API
 * to an object containing:
 * - a date string for the calendar
 * - whether the date string is representing an active date
 */
const convertEventDateTimeToCalendar = (
    date: string,
    time: string,
    timezoneOffset: number,
    isEndDate: boolean = false
): ICalendarTimeConversionResult => {
    // combine date input and time input
    const dateStr = date.replace('Z', '');
    let dateObj = DateUtility.toDayJS(dateStr);

    // In case there is no endTime, endDate has to increase by one day because:
    // For example, endDate is 2022-05-06T00:00:00, which means that the day has not started at all
    // So by increasing it by 1 day we get:
    // - An event that lasts the entire 2022-05-06 day
    // - It is marked as an "All day" event instead of an event that stops at 23:59:59
    if (isEndDate && !time) {
        dateObj = dateObj.date(dateObj.date() + 1);
    }

    // show the time of the event if time exists or if the event timezone is different from the current timezone
    // case showing time while time is empty -> default to 00:00
    //   case endDate of the case above, the date has already been compensated by 1 day so '00:00' is correct
    const showTime = !!time || timezoneOffset !== DateUtility.CurrentTimeZone;
    if (showTime) {
        dateObj = DateUtility.copyTime(dateObj, DateUtility.toDateByFormat(time || '00:00', DayJSDateStringFormat.HHMM));
    }

    // make the date time UTC + offset
    dateObj = DateUtility.convertToUTC(dateObj, -timezoneOffset);

    // establish the result
    const result: ICalendarTimeConversionResult = { isActiveDate: false, dateStr: '' };
    result.isActiveDate = DateUtility.compare(dateObj, DateUtility.toDayJS(new Date())) >= 0;
    if (showTime) {
        result.dateStr = dateObj.toISOString();
    } else {
        result.dateStr = DateUtility.toFormattedString(dateObj, DayJSDateStringFormat.YYYYMMDD);
    }

    return result;
};

/**
 * get the current month from the calendar view, based on
 * - the view type
 * - start date, end date of the date range shown on the current view
 * - the month being selected on the calendar's month picker <-- This only affects the week view
 */
const getCalendarViewCurrentMonth = (start: Date, end: Date, view: ViewApi, monthPickerMonth: number): string => {
    const newMonth = new Date();
    let targetMonth = newMonth.getMonth();
    // try to get the year from start or end, else default to the original value (current year)
    newMonth.setFullYear((start || end)?.getFullYear() || newMonth.getFullYear());
    if (!ObjectUtility.isNil(start) && !ObjectUtility.isNil(end)) {
        const startMonth = start.getMonth();
        const endMonth = end.getMonth();
        switch (view.type) {
            case CalendarViewType.DayGridMonth:
                if (endMonth - startMonth === 2) {
                    // case date range includes 3 different months
                    targetMonth = startMonth + 1;
                } else if (start.getDate() === 1) {
                    // case date range includes the first month and the first few days of the second month
                    targetMonth = startMonth;
                } else {
                    // case date range includes the last few days of first month and only the whole second month
                    targetMonth = endMonth;
                }
                break;
            case CalendarViewType.TimeGridWeek:
                // Note: toMonth is based on calendar month of 1-12
                // endMonth is based on JavaScript month of 0-11
                // So if we were to select a week like Mar 27 to Apr 2,
                // - if this week range is selected via the month picker and endMonth matches the "monthPickerMonth" from the picker
                //   then the endMonth will be seleted
                // - if this week range is selected via [today] button and endMonth === current month
                //   then the endMonth will be selected
                // - if this week range is selected via the arrow buttons [<][>]
                //   then the startMonth will be selected
                targetMonth = endMonth + 1 === monthPickerMonth || endMonth === newMonth.getMonth() ? endMonth : startMonth;
                break;
            default:
                targetMonth = startMonth;
                break;
        }
    }
    newMonth.setMonth(targetMonth);
    return DateUtility.toFormattedString(newMonth, DayJSDateStringFormat.YYYYMM);
};

interface ICalendarAppProps {
    linkToAddEvent: string;
    linkToEventListing: string;
    calendarRootId: number;
}

const QueryParams = {
    Month: 'calendarMonth',
    Category: 'calendarCategories'
};

enum CopyState {
    Idle,
    Copying,
    Copied,
    Error
}
const CopyLinkLabelDictionary = {
    [CopyState.Idle]: 'Copy link',
    [CopyState.Copying]: 'Copying...',
    [CopyState.Copied]: 'Copied!',
    [CopyState.Error]: 'Error!'
};

export const CalendarApp = ({ linkToAddEvent, linkToEventListing, calendarRootId }: ICalendarAppProps) => {
    const calendarRef = React.useRef<FullCalendar>();
    const today = new Date();
    const currentYear = DateUtility.getCurrentYear();

    // Handle checking query params - START
    // Reading query params
    const { calendarMonth, calendarCategories } = React.useMemo(() => {
        const urlParams = new URLSearchParams(window.location.search);
        return {
            calendarMonth: urlParams.get(QueryParams.Month) || null,
            calendarCategories: urlParams.get(QueryParams.Category) || null
        };
    }, []);

    // parsing default initial month
    const defaultInitialMonth = React.useMemo(() => {
        if (calendarMonth && DateUtility.isValidDateStringByFormat(calendarMonth, DayJSDateStringFormat.YYYYMM)) {
            return calendarMonth;
        }
        return DateUtility.toFormattedString(today, DayJSDateStringFormat.YYYYMM);
    }, [calendarMonth]);

    // parsing default categories (filter)
    const defaultFilter = React.useMemo(() => {
        if (calendarCategories && StringUtility.isString(calendarCategories)) {
            const reducer = (accumulator: Array<number>, current: string) => {
                const numericCategoryId = Number(current.trim());
                if (!isNaN(numericCategoryId)) {
                    accumulator.push(numericCategoryId);
                }
                return accumulator;
            };
            return calendarCategories.split(/,[/s/t]*/g).reduce(reducer, []);
        }
        return null;
    }, [calendarCategories]);
    // Handle checking query params - END

    // Handle setting a target month for calendar view - START
    const [initialMonth, setInitialMonth] = React.useState(defaultInitialMonth);
    const [monthPickerValue, setMonthPickerValue] = React.useState(initialMonth);

    const { toMonth, toYear } = React.useMemo(() => {
        if (!monthPickerValue) {
            return {
                toMonth: today.getMonth() + 1,
                toYear: today.getFullYear()
            };
        }
        const targetMonth = DateUtility.toDateByFormat(monthPickerValue, DayJSDateStringFormat.YYYYMM);
        return {
            toMonth: targetMonth.month() + 1,
            toYear: targetMonth.year()
        };
    }, [monthPickerValue]);

    const initialDate = React.useMemo(() => {
        if (!monthPickerValue) {
            return null;
        }
        return DateUtility.toDateByFormat(monthPickerValue, DayJSDateStringFormat.YYYYMM).toDate();
    }, [monthPickerValue]);

    const goToMonth = React.useCallback(() => {
        if (!initialDate) {
            return;
        }
        setInitialMonth(monthPickerValue);
        calendarRef.current.getApi().gotoDate(initialDate);
    }, [monthPickerValue]);
    // Handle setting a target month for calendar view - END

    // Handle getting categories - START
    const {
        data: categories,
        isLoading: isLoadingCategory,
        error: categoryError,
        isError: isCategoryError
    } = calendarApi.useGetCategoriesQuery(calendarRootId);

    const categoryErrorMessage = React.useMemo(() => {
        return ApiUtility.getFetchErrorMessage(categoryError as FetchBaseQueryError);
    }, [categoryError]);

    const categoryOptions = React.useMemo(() => {
        if (!categories || !categories.length) {
            return [];
        }

        // All the categories are in a single list, each one of them has
        // the name of the parent category. Here, we group each category
        // in it's respective parent category to show them grouped in the
        // dropdown
        const categoryGroups = {};
        categories.forEach((category) => {

            // We create the parent category, if we haven't
            if (!categoryGroups[category.parentCategoryName]) {
                categoryGroups[category.parentCategoryName] = {
                    label: category.parentCategoryName,
                    options:[]
                }
            }

            // Add the category to it's parent category
            categoryGroups[category.parentCategoryName].options.push({
                value: category.categoryId + '',
                label: category.categoryName,
            });
        });

        return Object.values(categoryGroups);
    }, [categories]);

    const defaultCategories = React.useMemo(() => {
        if (!defaultFilter || !defaultFilter.length || !categoryOptions || !categoryOptions.length) {
            return [];
        }

        // We search for the filtered categories in each parent. The defaultFilter comes
        // from the querystring when the user press the button 'Copy link' in the calendar
        const selectedOptions = []
        categoryOptions.forEach((group: IGroupSelectOption) => {

            const selectedOptionsForGroup = group.options.filter((category: ISelectOption) =>
                defaultFilter.some(categoryId => category.value === categoryId + '')
            )

            selectedOptions.push(...selectedOptionsForGroup);
        });

        return selectedOptions;

    }, [categoryOptions, defaultFilter]);
    // Handle getting categories - END

    // Handle checking permission to show navigation buttons - START
    const { data: permission } = calendarApi.useGetPermissionOnCalendarQuery(calendarRootId);

    const showNavButtons = React.useMemo(() => {
        if (!permission) {
            return false;
        }
        const { isUserLoggedIn, userHasPrivilege, isMemberLoggedIn, memberHasPrivilege, isAdmin } = permission;
        if ((isUserLoggedIn && userHasPrivilege) || (isMemberLoggedIn && memberHasPrivilege) || isAdmin) {
            return true;
        }
        return false;
    }, [permission]);
    // Handle checking permission to show navigation buttons - END

    // Handle getting calendar events - START
    // getEvents API
    const [getEvents, { data: eventsData, isError: isGetEventError, error: getEventError, isLoading: isQueringEvents }] =
        calendarApi.useGetEventsMutation();

    const getEventErrorMessage = React.useMemo(() => {
        return ApiUtility.getFetchErrorMessage(getEventError as FetchBaseQueryError);
    }, [getEventError]);

    // filter value (by categories)
    const [tempFilter, setTempFilter] = React.useState(null);
    const [filter, setFilter] = React.useState(defaultFilter);

    // timeframe for the list of events (controlled by the calendar)
    const [timeframe, setTimeframe] = React.useState(null);

    // calling getEvents API based on the timeframe's and filter's values
    React.useEffect(() => {
        if (!timeframe) {
            return;
        }

        const commonQueryParams = {
            calendarNodeId: calendarRootId,
            startDateInput: timeframe.start,
            endDateInput: timeframe.end
        };
        if (filter && filter.length) {
            getEvents({
                ...commonQueryParams,
                filter: FilterEventEnum.Categories,
                categoryIds: filter.join(',')
            });
            return;
        }
        getEvents({
            ...commonQueryParams,
            filter: FilterEventEnum.AllEvents
        });
    }, [timeframe?.start, timeframe?.end, filter]);

    // Returns the color of the event based on the list of categories it belongs to
    const getEventColor = (eventCategories: string) => {
        let eventColor = '#2B5891';
        const categoriesNamesArray = eventCategories.split(',');
        if (categoriesNamesArray.length > 0) {
            const firstCategoryName = categoriesNamesArray[0];
            const firstCategoryObject = categories.find((c) => { return c.categoryName === firstCategoryName });
            if (firstCategoryObject) {
                eventColor = firstCategoryObject.color;
            }
        }

        return eventColor;
    }

    // event source object used by full calendar
    // this includes event data transform function to convert API event object to Full Calendar's event model
    const eventSource: EventSourceInput = React.useMemo(
        () => ({
            events: function (info, successCallback, failureCallback) {
                const start = DateUtility.toFormattedString(info.start, DayJSDateStringFormat.YYYYMMDD);
                const end = DateUtility.toFormattedString(info.end, DayJSDateStringFormat.YYYYMMDD);
                if (!timeframe || timeframe.start !== start || timeframe.end !== end) {
                    setTimeframe({
                        start,
                        end
                    });
                }
                successCallback(eventsData || []);
            },
            eventDataTransform: function (eventData: Omit<ICalendarEvent, 'id'>) {
                const standardEventObject: EventInput = { extendedProps: {} };
                const { eventInformation, eventLocation, url } = eventData;

                // setup event start, end
                const {
                    startDate,
                    startTime,
                    endDate,
                    endTime,
                    eventTitle,
                    shortEventTitle,
                    timezoneInput,
                    categories,
                    webPageLink,
                    description
                } = eventInformation || {};
                if (!startDate || !endDate) {
                    return null;
                }

                const timezoneOffset = Number(timezoneInput);
                standardEventObject.start = convertEventDateTimeToCalendar(startDate, startTime, timezoneOffset).dateStr;
                standardEventObject.end = convertEventDateTimeToCalendar(endDate, endTime, timezoneOffset, true).dateStr;

                // setup event title and url
                standardEventObject.title = shortEventTitle;
                standardEventObject.url = url;

                // setup event color
                // For the List view the bullet is set automatically, in order to set its color
                // we have to change the color property directly. In the other hand, we have to render
                // the bullet in the other views, and the color property is used to set the
                // background color. Thats why we set a default color and set a different property for
                // the categories in the rest of the views for the calendar.
                // Besides this, it is necesary to disable lazy loading because we need to force the call
                // of this callback in order to update the color properties of the events
                if (calendarRef.current.getApi().view.type === CalendarViewType.ListMonth) {
                    standardEventObject.color = getEventColor(categories);
                }
                else {
                    standardEventObject.color = '#D8D8D8';
                    standardEventObject.textColor = '#4D595C';
                    standardEventObject.extendedProps.eventCategoryColor = getEventColor(categories);
                }

                // We have to set explicitly the block view
                // because in 'month' view some events where not
                // being painted with the corresponding color,
                // the reazon is not clear, but it seems is
                // it happens when the end time is not defined for
                // the event
                standardEventObject.display = 'block';

                // setup event details
                standardEventObject.extendedProps.categories = categories;
                standardEventObject.extendedProps.moreInfo = webPageLink;
                standardEventObject.extendedProps.eventTitle = eventTitle;

                const locationObj = eventLocation || {};
                const location = Object.keys(locationObj).reduce((accumulator, currentKey) => {
                    const currentLocationValue = locationObj[currentKey];
                    if (!currentLocationValue) {
                        return accumulator;
                    }
                    const trimmed = currentLocationValue.trim();
                    if (trimmed) {
                        accumulator += accumulator ? `, ${trimmed}` : trimmed;
                    }
                    return accumulator;
                }, '');

                standardEventObject.extendedProps.location = locationObj.locationName;
                standardEventObject.extendedProps.locationDetail = location;
                standardEventObject.extendedProps.eventDetail = description;

                return standardEventObject;
            }
        }),
        [eventsData]
    );

    // Handle getting calendar events - END

    // Handle copy link - START
    const [copyState, setCopyState] = React.useState(CopyState.Idle);

    // Creates a link with the current filters
    const buildCalendarLink = (initialMonth, filter) => {

        const params = {};
        if (initialMonth) {
            params[QueryParams.Month] = initialMonth;
        }
        if (filter && filter.length) {
            params[QueryParams.Category] = filter.join(',');
        }
        const url = `${location.protocol}//${location.host}${location.pathname}${ApiUtility.getQueryParams(params)}${location.hash
            }`;

        return url;
    };

    const copyLink = React.useCallback(async () => {

        const url = buildCalendarLink(initialMonth, filter);

        setCopyState(CopyState.Copying);
        const copySuccessful = await ClipboardUtility.copy(url);
        if (copySuccessful) {
            setCopyState(CopyState.Copied);
        } else {
            setCopyState(CopyState.Error);
        }
    }, [initialMonth, filter]);

    React.useEffect(() => {
        let timeout = null;
        if (copyState === CopyState.Copied || CopyState.Error) {
            timeout = window.setTimeout(() => {
                setCopyState(CopyState.Idle);
            }, 2000);
        }
        return () => {
            if (timeout) {
                window.clearTimeout(timeout);
                timeout = null;
            }
        };
    }, [copyState]);

    // Handle copy link - END

    // Handle Social Links - START

    React.useEffect(() => {

        // When the initialMonth and category filter are updated
        // we proceed to update the addthis url

        // Create the new URL with the filters and assign it to addthis
        const url = buildCalendarLink(initialMonth, filter);
        if (window["addthis_share"]) { 
            window['addthis_share'].url = url;
        }

        // Refresh the internal state of addthis
        // so the new URL takes effect
        if (window["addthis"]) {
            window['addthis'].toolbox('.addthis_inline_share_toolbox');
        }

    }, [initialMonth, filter]);

    // Handle Social Links - END

    // START RENDERING VIEWS
    // Case loading
    if (isLoadingCategory) {
        return (
            <div className='form-state-block d-flex align-items-center justify-content-center'>
                <Spinner />
            </div>
        );
    } else if (isCategoryError) {
        // Case error with getting categories
        return (
            <Alert type={AlertType.Error}>
                <div>Something went wrong while getting the list of categories!</div>
                {categoryErrorMessage}
            </Alert>
        );
    }

    const calendarLegend = categories.filter(categorie => categorie.color != "").map((value) =>
        <div className="calendar-legend__item">
            <span className="calendar-legend__dot" style={{ borderColor: value.color }} ></span> 
            <p className="calendar-legend__text">{value.categoryName}</p> 
        </div>
    );

    // Case showing calendar
    return (
        <>
            {isGetEventError ? (
                <Alert type={AlertType.Error}>
                    <div>Something went wrong while getting the list of events!</div>
                    {getEventErrorMessage}
                </Alert>
            ) : (
                ''
            )}
            {/* Row 1 - Month Picker & Category filter */}

            {isQueringEvents ? (<Spinner isOverlay={true} />) : ('')}

            <div className='row flex-md-nowrap justify-content-between align-items-start mb-2 calendar-header_dropdown-wrapper'>
                <div className='col col-12 mb-2 mb-md-0 col-md-auto d-flex'>
                    <DatePicker
                        isMonthPicker={true}
                        defaultValue={{ month: toMonth, year: toYear }}
                        onChange={value => {
                            if (value) {
                                setMonthPickerValue(value);
                            }
                        }}
                        yearRange={{
                            start: currentYear - 2,
                            end: currentYear + 5
                        }}
                    />
                    <Button onClick={goToMonth} className='m-0' disabled={!initialDate}>
                        Go
                    </Button>
                </div>
                <div className='col col-12 col-md-auto flex-shrink-1'>
                    <Select
                        placeholder='Filter By'
                        options={categoryOptions}
                        defaultValue={defaultCategories}
                        isMulti={true}
                        closeMenuOnSelect={false}
                        blurInputOnSelect={false}
                        onChange={(value: ISelectOption[], action: any) => {
                            setTempFilter(value.map(option => option.value));
                        }}
                        onBlur={() => {
                            setFilter(tempFilter);
                        }}
                        styles={{
                            menu: provided => ({
                                ...provided,
                                width: 'auto',
                                minWidth: 200
                            }),
                            control: (provided, state) => ({
                                ...provided,
                                width: 'auto',
                                minWidth: 200,
                                ...UskinnedSelectStyles.control(provided, state)
                            })
                        }}
                    />
                </div>
            </div>

            {/* Row 2 - Copy link button & Navigation button */}
            <div className='d-flex align-items-center justify-content-between mb-2 calendar-header_button-wrapper'>
                <Button onClick={copyLink} disabled={copyState !== CopyState.Idle}>
                    {CopyLinkLabelDictionary[copyState]}
                </Button>

                <div className="addthis_inline_share_toolbox"></div>

                {showNavButtons && (linkToAddEvent || linkToEventListing) ? (
                    <div className='ml-2 d-flex align-items-center'>
                        {linkToAddEvent ? <LinkButton href={linkToAddEvent}>Add event</LinkButton> : ''}

                        {linkToEventListing ? (
                            <LinkButton className='ml-2' href={linkToEventListing}>
                                Event listing
                            </LinkButton>
                        ) : (
                            ''
                        )}
                    </div>
                ) : (
                    ''
                )}
            </div>
            <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, listPlugin, momentPlugin]}
                initialDate={initialDate}
                initialView={CalendarViewType.ListMonth}
                headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: [
                        CalendarViewType.DayGridMonth,
                        CalendarViewType.TimeGridWeek,
                        CalendarViewType.TimeGridDay,
                        CalendarViewType.ListMonth
                    ].join(',') // no spacing after comma is necessary
                }}
                datesSet={({ start, end, view }) => {
                    // Listening to date change event
                    // Update the "initialMonth" used for the copyLink feature
                    const newMonth = getCalendarViewCurrentMonth(start, end, view, toMonth);
                    setInitialMonth(newMonth);
                }}
                eventSources={[eventSource]}
                eventContent={EventContentRenderer}
                contentHeight='auto'
                eventClick={event => {
                    // NOTE: can possibly replace this by having custom link in custom view
                    // for month/week/day view

                    // For the view List Month we have a custom html with More Info Link
                    // in that case we have to check where the click was made,
                    // to do the propper redirect.
                    //
                    // If the clicked html component is a link we go to its href.
                    // otherwise we continue the normal flow to open the event detail page
                    if (event.view.type == CalendarViewType.ListMonth) {
                        event.jsEvent.preventDefault();
                        var element = event.jsEvent.target as any;
                        if (element.tagName === 'A') {
                            window.open(element.href, '_blank');
                        }
                    }

                    // For any other view different to the List Month
                    // we open the event link
                    if (event.event.url) {
                        event.jsEvent.preventDefault();
                        window.open(event.event.url, '_blank');
                    }
                }}
                listDayFormat= 'dddd, MMMM Do'
                listDaySideFormat={ false }
                lazyFetching={ false }
            />

            <div className='calendar-legend'>
                {calendarLegend}
            </div>

        </>
    );
};

export class Calendar {
    constructor(element: HTMLElement) {
        const store = createStore();

        const calendarRootId = Number(element.dataset.calendarRootId);
        if (!calendarRootId || isNaN(calendarRootId)) {
            console.log(`invalid calendar root node id: ${calendarRootId}`);
            return;
        }

        const linkToAddEvent = element.dataset.linkToAddEvent;
        const linkToEventListing = element.dataset.eventListingUrl;
        ReactDOM.render(
            <Provider store={store}>
                <ErrorBoundary>
                    <CalendarApp {...{ linkToAddEvent, linkToEventListing, calendarRootId }} />
                </ErrorBoundary>
            </Provider>,
            element
        );
    }
}
